/**
 * Runtime configuration and interceptors for the generated client — §16 of
 * swagger-codegen-plan.md.
 *
 * The SDK functions in `./gen/sdk.gen` all route through the single client instance in
 * `./gen/client.gen`. This module is what turns that bare instance into *our* client: it
 * adds the XSRF header, the error notifications, the Sentry capture and the analytics
 * counter that `fetchEGate` in helpers/eGateApi.ts does by hand.
 *
 * **Import order matters.** Nothing here runs unless this module is evaluated, so callers
 * must reach the SDK through `./index` (or `./queries`), both of which import this first.
 * Importing `./gen/sdk.gen` directly gets an unconfigured client — eslint's
 * `no-restricted-imports` blocks that.
 *
 * `client.test.ts` pins the error-branch behaviour against what `fetchEGate` does today;
 * change one, change both.
 *
 * What is deliberately *not* handled here — these keep using `helpers/eGateApi.ts`:
 *   - `linkEGate` (building a URL for an <a href> or a download)
 *   - `uploadFile` / `uploadFileDirect` (multipart and raw-body posts)
 *   - protobuf / CSV / `byte[]` endpoints, which the document cannot type usefully
 */
import * as Sentry from "@sentry/browser";
import { notification } from "antd";
import { t } from "i18next";
import { client } from "./gen/client.gen";
import { type ApiError, GetXsrfToken, XSRF_TOKEN_NAME } from "../helpers/eGateApi";
import { responseErrorNotification } from "../helpers/responseErrorNotification";
import { trackError } from "../helpers/analytics";

/**
 * Operation urls already carry `/api/v1/…`, so the base is just the origin.
 *
 * The fallback is for tests, which import this module without a DOM. It has to be an
 * absolute URL: the client builds a `Request`, and Node rejects a relative one where a
 * browser would accept it. The value is never used in the browser and never reaches the
 * network — tests stub `fetch`.
 */
const BASE_URL = typeof window !== "undefined" ? window.location.origin : "http://localhost";

/**
 * Opt a single call out of the error notification, replacing `fetchEGate`'s
 * `showNotification: false`.
 *
 *     geozonesCreateEntity({ body, ...silent })
 *
 * Sent as a header rather than a request option because the option types are closed —
 * arbitrary keys are a compile error and never reach the interceptor. Headers do, and the
 * request interceptor strips this one before the request goes out.
 */
export const SILENT_HEADER = "x-egate-silent";
export const silent = { headers: { [SILENT_HEADER]: "1" } } as const;

/**
 * Per-request state, correlated request → error interceptor by the `Request` object
 * itself. The client passes the very object the request interceptor returned, so a
 * WeakMap keys on it directly and nothing has to be cleaned up.
 */
interface PendingRequest {
  silent: boolean;
  /**
   * A clone taken before `fetch` consumes the body, so the Sentry context on a 500 can
   * still report the payload. Cloned rather than read: teeing the stream is cheap and
   * happens per mutation, whereas reading it to a string would cost on every large
   * import even though only a 500 ever needs it.
   */
  clone: Request | undefined;
}
const pending = new WeakMap<Request, PendingRequest>();

/**
 * `mode` and `baseUrl` only. The generated defaults already supply
 * `Content-Type: application/json` and a query serializer that emits
 * `form`/`explode` (`?a=1&a=2`) — the same shape `qs.stringify(q, { arrayFormat: "repeat" })`
 * produced, and what the server model binder expects.
 *
 * 🔴 Do **not** add a `fetch` option here. `client.gen.ts` resolves
 * `options.fetch ?? _config.fetch ?? globalThis.fetch` per call, and
 * `containers/App/AutomaticLogout.tsx` monkey-patches `globalThis.fetch` via
 * `fetch-intercept` *during render* — after this module has loaded. Pinning `fetch` here
 * would freeze the pre-patch reference and silently disable inactivity tracking and
 * **automatic logout on 401**. `client.test.ts` guards this by swapping the global
 * mid-suite.
 */
client.setConfig({ baseUrl: BASE_URL, mode: "cors" });

client.interceptors.request.use((request) => {
  const isMutation = request.method !== "GET";
  if (isMutation) request.headers.set(XSRF_TOKEN_NAME, GetXsrfToken());

  pending.set(request, {
    silent: request.headers.get(SILENT_HEADER) === "1",
    clone: isMutation ? request.clone() : undefined,
  });
  request.headers.delete(SILENT_HEADER);
  return request;
});

/**
 * The client throws the parsed error body internally and routes it here before returning
 * it as `{ error }`, so this runs on exactly the failure path and the body is already
 * parsed — no second read of the stream.
 *
 * `raw` is the JSON body when it parsed, and the raw text when it did not. Branching on
 * that rather than on `Content-Type` also covers a problem-details body served as
 * `text/plain`, which the header check used to miss.
 */
client.interceptors.error.use(async (raw, response, request, options) => {
  // A network failure or an error thrown while building the request never produced a
  // response; there is nothing to classify, so hand it back untouched.
  if (!response || response.ok) return raw;

  const entry = request ? pending.get(request) : undefined;

  // Usage analytics failure counter; 401 (session expiry) is noise, not signal.
  // `options.url` is the OpenAPI template, not the interpolated URL — unlike the
  // `endpoint` string this replaces, it does not create one metric series per id. The
  // concrete path (no query string) goes on the log only.
  if (response.status !== 401) {
    trackError(
      "api.error",
      { status: response.status, endpoint: options.url },
      { path: request ? new URL(request.url).pathname : undefined }
    );
  }

  const quiet = entry?.silent ?? false;

  const captureIf500 = async (detail: string, body: unknown) => {
    if (response.status !== 500) return undefined;
    const payload = await entry?.clone?.text().catch(() => undefined);
    return Sentry.captureException(new Error(detail), {
      contexts: {
        apiError: {
          status: response.status,
          endpoint: request?.url,
          payload,
          query: options.query,
          detail,
          body,
        },
      },
    });
  };

  if (raw !== null && typeof raw === "object") {
    const body = raw as Record<string, unknown>;
    const detail = (body.detail ?? body.title ?? response.statusText) as string;
    const sentryId = await captureIf500(detail, body);

    if (!quiet) {
      if (body.errors) {
        notification.error({ message: Object.values(body.errors).join(", "), placement: "top" });
      } else if (body.detail) {
        notification.error({ message: detail, placement: "top" });
      }
    }
    // `status` comes from the problem-details body, not the response — matching
    // fetchEGate, whose call sites read the body's status.
    return {
      status: body.status as number | undefined,
      errors: body.errors as ApiError["errors"],
      type: body.type as string | undefined,
      detail,
      sentryId,
    } satisfies ApiError;
  }

  const text = typeof raw === "string" ? raw : "";
  // Only the non-JSON branch raises the "not authorized" notification. That asymmetry
  // is inherited from fetchEGate, not a decision made here — see client.test.ts.
  if (response.status === 403) responseErrorNotification({ status: 403 }, t);
  const sentryId = await captureIf500(response.statusText, text);
  if (!quiet && text) notification.error({ message: text, placement: "top" });

  return {
    status: response.status,
    errors: ["API error"] as unknown as ApiError["errors"],
    type: "",
    detail: text,
    sentryId,
  } satisfies ApiError;
});

export { client };

/** What an SDK function resolves to when `throwOnError` is left at its default. */
interface SdkResult {
  data?: unknown;
  error?: unknown;
  request?: Request;
  response?: Response;
}

/**
 * The success payload of an SDK result.
 *
 * Written as a conditional rather than as an `unwrap<T>(…: Promise<{ data?: T }>)`
 * parameter because the result is a *union* — `{ data, error: undefined }` on success and
 * `{ data: undefined, error }` on failure. Inferring `T` from both arms yields `unknown`;
 * distributing over the union and dropping the failure arm's `undefined` yields the type
 * the endpoint actually returns.
 */
type ResultData<R> = R extends { data?: infer D } ? Exclude<D, undefined> : never;

/**
 * Imperative path: give me the data or throw the `ApiError` shape call sites destructure.
 *
 *     const geozone = await unwrap(geozonesGetEntity({ path: { id } }));
 *
 * The react-query *hook* path does not need this — the generated `…Options()` helpers
 * already throw the `error` the interceptor above produced. Use `unwrap` for mutations and
 * one-off calls.
 *
 * Normalising an empty body to `{}` is the part that matters, and it is `toJson`'s rule
 * verbatim (apiHelpers.ts:30-33): zero length means `{}` *whatever* the content type. The
 * client only does that for JSON — a 200 with `Content-Length: 0` and a text content type
 * yields `""`, and call sites destructure the result of a delete or a no-content PUT,
 * where `const { a } = ""` gives undefined properties instead of the empty object they
 * were written against.
 */
export async function unwrap<R extends SdkResult>(call: Promise<R>): Promise<ResultData<R>> {
  const { data, error, response } = await call;
  if (response?.ok) {
    if (response.status === 204 || response.headers.get("Content-Length") === "0") {
      return {} as ResultData<R>;
    }
    return (data ?? {}) as ResultData<R>;
  }
  throw (
    error ?? {
      status: response?.status ?? 500,
      errors: ["unknown error"] as unknown as ApiError["errors"],
      type: "",
      detail: response?.statusText ?? "network error",
    }
  );
}
